import { createServerFn } from '@tanstack/react-start'
import { notFound } from '@tanstack/react-router'
import {
  DropifyApiError,
  getStorefrontBlogPost,
  listStorefrontBlogPosts,
  normalizeBlogPost,
} from './dropify'
import type { StorefrontBlogPost } from './dropify'
import { cleanNewsPostCopy } from './newsCopy'

export const NEWS_CATEGORY_SLUG = 'berita'
export const HOMEPAGE_NEWS_LIMIT = 6
const FALLBACK_FETCH_LIMIT = 100

export type NewsArchiveParams = {
  page?: number
  per_page?: number
}

export type NewsArchivePage = {
  items: StorefrontBlogPost[]
  page: number
  per_page: number
  total: number
  total_pages: number
}

export function canUseUnfilteredNewsFallback(error: unknown) {
  return error instanceof DropifyApiError
    && ([400, 404, 422].includes(error.status) || error.status >= 500)
}

function categorySlugs(record: Record<string, unknown>): string[] {
  if (!Array.isArray(record.categories)) return []

  return record.categories.flatMap((category) => {
    if (typeof category === 'string') return [category.toLowerCase()]
    if (!category || typeof category !== 'object') return []

    const value = category as { name?: unknown; slug?: unknown }
    const slug = typeof value.slug === 'string' ? value.slug : value.name
    return typeof slug === 'string' ? [slug.toLowerCase()] : []
  })
}

export function selectNewsRecords(records: Record<string, unknown>[]) {
  const recordsWithCategories = records.filter((record) => categorySlugs(record).length > 0)

  // The public API currently omits category relations from list responses.
  // When metadata is present, enforce the category locally. When it is absent,
  // trust the upstream category endpoint or use the unfiltered recovery result.
  if (recordsWithCategories.length === 0) return records

  return records.filter((record) => categorySlugs(record).includes(NEWS_CATEGORY_SLUG))
}

function newestFirst(posts: StorefrontBlogPost[]) {
  return [...posts].sort((left, right) => {
    const leftTime = left.published_at ? Date.parse(left.published_at) : 0
    const rightTime = right.published_at ? Date.parse(right.published_at) : 0
    return rightTime - leftTime
  })
}

async function fetchDashboardNewsPage(params: NewsArchiveParams = {}): Promise<NewsArchivePage> {
  const page = Math.max(1, Math.floor(params.page ?? 1))
  const perPage = Math.min(24, Math.max(1, Math.floor(params.per_page ?? HOMEPAGE_NEWS_LIMIT)))
  let records: Record<string, unknown>[]

  try {
    const response = await listStorefrontBlogPosts({
      category: NEWS_CATEGORY_SLUG,
      page,
      per_page: perPage,
    })
    records = selectNewsRecords(response.items)
    return {
      items: newestFirst(records.map((record) => cleanNewsPostCopy(normalizeBlogPost(record)))),
      page: response.page,
      per_page: response.per_page,
      total: response.total,
      total_pages: response.total_pages,
    }
  } catch (error) {
    // The category-filtered endpoint can fail while the unfiltered list stays
    // available. Recover from filter validation errors and server errors, then
    // filter locally whenever category metadata is present.
    if (!canUseUnfilteredNewsFallback(error)) throw error

    // Keep the homepage useful while the upstream category filter is
    // unavailable. The larger recovery page lets us filter locally once the
    // API includes category metadata in its list response.
    const response = await listStorefrontBlogPosts({
      page: 1,
      per_page: FALLBACK_FETCH_LIMIT,
    })
    records = selectNewsRecords(response.items)
  }

  const allPosts = newestFirst(records.map((record) => cleanNewsPostCopy(normalizeBlogPost(record))))
  const start = (page - 1) * perPage

  return {
    items: allPosts.slice(start, start + perPage),
    page,
    per_page: perPage,
    total: allPosts.length,
    total_pages: Math.max(1, Math.ceil(allPosts.length / perPage)),
  }
}

export const listDashboardNewsPosts = createServerFn({ method: 'GET' })
  .handler(async () => (await fetchDashboardNewsPage()).items)

export const listDashboardNewsArchive = createServerFn({ method: 'GET' })
  .inputValidator((params?: NewsArchiveParams) => params ?? {})
  .handler(({ data }) => fetchDashboardNewsPage(data))

export const getDashboardNewsPost = createServerFn({ method: 'GET' })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data }) => {
    try {
      return cleanNewsPostCopy(normalizeBlogPost(await getStorefrontBlogPost(data)))
    } catch (error) {
      if (error instanceof DropifyApiError && error.status === 404) throw notFound()
      throw error
    }
  })

export const listAllDashboardNewsSlugs = createServerFn({ method: 'GET' })
  .handler(async () => {
    const page = await fetchDashboardNewsPage({ page: 1, per_page: 24 })
    if (page.total_pages <= 1) return page.items.map(({ slug, published_at }) => ({ slug, published_at }))

    const pages = await Promise.all(
      Array.from({ length: page.total_pages - 1 }, (_, index) =>
        fetchDashboardNewsPage({ page: index + 2, per_page: 24 }),
      ),
    )
    return [page, ...pages]
      .flatMap((item) => item.items)
      .map(({ slug, published_at }) => ({ slug, published_at }))
  })
